import React, { useState, useEffect, useCallback } from 'react';
import { CreditCard, CheckSquare, Square, AlertOctagon, X, Power, PowerOff, Ban, Trash2, RotateCcw, Banknote, RefreshCw } from 'lucide-react';
import { colors, API, fmt, faseColor, statusColor, riscoColor, authHeaders, isAdmin, podeGerenciarCartoes, getTitularId, veNegociacao } from '../utils/theme';
import {
  PageHeader, SearchBar, Select, Table, Th, Td, TrHover, Badge, ActionButtons,
  Modal, FormField, Input, TextArea, Button, LoadingSpinner,
  ConfirmDialog, EmptyState,
} from '../components/UI';
import useSort from '../hooks/useSort';
import useIsMobile from '../hooks/useIsMobile';
import OpenFinanceCartao from '../components/OpenFinanceCartao';
import TotaisDeLimite, { somaDe } from '../components/TotaisDeLimite';

// De onde vem o utilizado/disponível do cartão (02/11/2026) — vira o "title"
// das células e a explicação embaixo do campo no formulário.
const mesBR = (m) => (m ? `${m.slice(5, 7)}/${m.slice(0, 4)}` : '');
function explicarUtilizado(c) {
  if (!c) return '';
  if (c.limite_fonte === 'open_finance') return 'Limite e disponível informados pelo banco (Open Finance).';
  if (c.limite_fonte === 'informado') {
    return `Disponível informado à mão: ${fmt(c.disponivel_informado_valor)}. Compras lançadas depois descontam, `
      + 'pagamentos depois devolvem. Volta ao automático quando chegar a próxima fatura.';
  }
  const k = c.limite_composicao;
  if (!k) return '';
  const partes = k.mes_fatura
    ? [`fatura ${mesBR(k.mes_fatura)} em aberto ${fmt(k.em_aberto_fatura)}`]
    : ['sem fatura carregada'];
  if (k.lancado_depois) partes.push(`+ ${fmt(k.lancado_depois)} lançados no TechMiles depois`);
  if (k.pago_depois) partes.push(`− ${fmt(k.pago_depois)} pagos depois`);
  return `Automático: ${partes.join(' ')}.`;
}

function OrigemDoDisponivel({ cartao, tocado }) {
  const estilo = { fontSize: 11, color: colors.textMuted, marginTop: 4, lineHeight: 1.4 };
  if (tocado) {
    return <div style={{ ...estilo, color: colors.warning }}>
      Ao salvar, este valor vira o disponível manual a partir de agora: compras lançadas depois descontam e ele vale
      até a próxima fatura chegar. Deixe em branco pra voltar ao automático.
    </div>;
  }
  if (!cartao) return null;
  if (cartao.limite_fonte === 'open_finance') {
    return <div style={estilo}>Cartão no Open Finance: vale o disponível do banco ({fmt(cartao.disponivel_efetivo)}).</div>;
  }
  return <div style={estilo}>
    Hoje: {fmt(cartao.disponivel_efetivo)} disponível. {explicarUtilizado(cartao)}
  </div>;
}

// Totais dos cartões que estão na lista — mudam com os filtros e a busca.
function TotaisDaLista({ cartoes }) {
  const conectados = cartoes.filter(c => c.limite_fonte === 'open_finance').length;
  return (
    <TotaisDeLimite limite={somaDe(cartoes, 'limite_efetivo')} utilizado={somaDe(cartoes, 'utilizado_efetivo')}
      disponivel={somaDe(cartoes, 'disponivel_efetivo')}
      subtitulo={`${cartoes.length} cartão(ões) na lista${conectados ? ` · ${conectados} com limite do banco` : ''}`} />
  );
}

// Mapa de todos os valores que vêm do banco/Manager → label PT-BR + cor
const STATUS_MAP = {
  // Português
  'ATIVO':     { label: 'Ativo',     cor: 'success' },
  'INATIVO':   { label: 'Inativo',   cor: 'muted'   },
  'CANCELADO': { label: 'Cancelado', cor: 'danger'  },
  'BLOQUEADO': { label: 'Bloqueado', cor: 'warning' },
  'SUSPENSO':  { label: 'Suspenso',  cor: 'warning' },
  // Inglês (vindo da API XMiles Manager)
  'ACTIVE':    { label: 'Ativo',     cor: 'success' },
  'INACTIVE':  { label: 'Inativo',   cor: 'muted'   },
  'SUSPENDED': { label: 'Suspenso',  cor: 'warning' },
  'CANCELLED': { label: 'Cancelado', cor: 'danger'  },
  'CANCELED':  { label: 'Cancelado', cor: 'danger'  },
  'BLOCKED':   { label: 'Bloqueado', cor: 'warning' },
};

// Opções únicas para o filtro (agrupando EN/PT pelo mesmo label).
// "Em Negociação" é pseudo-status — vem da flag gerenciamento_crise, não de status.
// Filtro "Ativo" exclui cartões em negociação (vide STATUSES_FILTRO/.filter abaixo).
const STATUSES_FILTRO = [
  { value: 'ativo',      label: 'Ativo',         values: ['ATIVO','ACTIVE'] },
  { value: 'negociacao', label: 'Em Negociação', values: [] },
  { value: 'suspenso',   label: 'Suspenso',      values: ['SUSPENSO','SUSPENDED'] },
  { value: 'bloqueado',  label: 'Bloqueado',     values: ['BLOQUEADO','BLOCKED'] },
  { value: 'inativo',    label: 'Inativo',       values: ['INATIVO','INACTIVE'] },
  { value: 'cancelado',  label: 'Cancelado',     values: ['CANCELADO','CANCELLED','CANCELED'] },
];

function statusLabel(s) { return STATUS_MAP[s?.toUpperCase()]?.label || s || '-'; }
function statusCorClass(s) { return STATUS_MAP[s?.toUpperCase()]?.cor || 'muted'; }
function statusColorFromClass(cls) {
  if (cls === 'success') return colors.success;
  if (cls === 'danger')  return colors.danger;
  if (cls === 'warning') return colors.warning;
  return colors.textDim;
}

const STATUSES = ['ATIVO', 'INATIVO', 'CANCELADO', 'BLOQUEADO'];

// Tags locais (ortogonais ao status do Manager). Não vão pro Manager nem
// são sobrescritas pelo syncCards.
const TAGS_LOCAIS = [
  { value: 'BLOQUEADO_USO', label: 'Bloqueado p/ uso', color: '#ef4444' },
  { value: 'RESERVA',       label: 'Reserva',          color: '#94a3b8' },
  { value: 'EM_ANALISE',    label: 'Em análise',       color: '#f59e0b' },
];
const TAG_BY_VALUE = Object.fromEntries(TAGS_LOCAIS.map(t => [t.value, t]));
const TIPOS_CARTAO = ['CREDITO', 'DEBITO', 'CONSIGNADO', 'PRE-PAGO'];
const STATUS_ACORDO = ['SEM_ACORDO', 'EM_NEGOCIACAO', 'PENDENTE', 'ACEITO', 'RECUSADO'];
const STATUS_PAGAMENTO = ['NÃO PAGO', 'PAGO MÍNIMO', 'PAGO PARCIAL', 'PAGO TOTAL', 'ACORDO'];
const TIPOS_CONTA = ['Corrente', 'Poupança', 'Salário', 'Digital'];

const emptyForm = {
  // Dados Básicos
  titular_id: '', banco_id: '', nome_cartao: '', tipo_cartao: 'CREDITO', bandeira: '',
  numeracao_final: '', limite_total: '', limite_disponivel: '', fatura_atual: '', pgto_minimo: '',
  melhor_dia_compra: '', data_vencimento: '', data_validade: '', data_ultimo_pgto: '',
  dias_atraso: '0', status_pagamento: 'NÃO PAGO', status: 'ATIVO',
  serasa: 'NÃO', protesto: 'NÃO', classificacao_xmiles: '', observacoes: '',
  // Financeiro
  valor_minimo_fatura: '', taxa_juros_rotativo_mensal: '', taxa_juros_rotativo_anual: '',
  taxa_juros_parcelamento: '', valor_anuidade: '', data_contratacao: '', numero_conta: '',
  agencia: '', tipo_conta: '', programa_pontos: '', pontos_acumulados: '',
  // Crise / Negociação
  nivel_escalacao: '0', rodada_negociacao: '0', data_ultima_negociacao: '', data_proxima_rodada: '',
  protocolo_sac: '', protocolo_ouvidoria: '', numero_caso_consumidor_gov: '', numero_caso_procon: '',
  proposta_acordo_valor: '', proposta_acordo_desconto_pct: '', proposta_acordo_parcelas: '',
  status_acordo: 'SEM_ACORDO', data_acordo: '', valor_divida_original: '', valor_divida_atualizada: '',
  // Legal
  processo_judicial: '', citacao_recebida: 'NÃO', data_citacao: '', advogado_responsavel: '',
  ded_ddc_solicitado: 'NÃO', ded_ddc_recebido: 'NÃO', data_solicitacao_ded: '',
  // Classificação
  prioridade_pagamento: '3', risco: 'BAIXO', categoria_estrategia: '', bloqueado_desde: '', motivo_bloqueio: '',
  origem: 'XMILES', categoria_id: '',
  // Flags
  gerenciamento_crise: 'NÃO', judicializado: 'NÃO',
};

// Crise, Legal e Classificação (prioridade, risco, estratégia, bloqueio) só o
// ADMIN vê (03/11/2026): mostram que há cartão nessa condição.
const TABS = [
  { id: 'basico', label: 'Dados Básicos' },
  { id: 'financeiro', label: 'Financeiro' },
  { id: 'crise', label: 'Crise / Negociação', soAdmin: true },
  { id: 'legal', label: 'Legal', soAdmin: true },
  { id: 'classificacao', label: 'Classificação', soAdmin: true },
  { id: 'openfinance', label: 'Open Finance' },
];

function DedupResultado({ resultado, onClose }) {
  const [showMesclados, setShowMesclados] = useState(false);
  const [showOrfaos, setShowOrfaos]       = useState(false);
  const accent = resultado.dry_run ? colors.info : colors.success;
  const mesclados = resultado.detalhes?.merged || [];
  const orfaos    = resultado.detalhes?.orphans || [];

  return (
    <div style={{
      marginBottom: 16, borderRadius: 10, border: `1px solid ${accent}40`,
      background: `${accent}08`, overflow: 'hidden',
    }}>
      {/* Header com stats */}
      <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 0, flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: accent, marginRight: 20 }}>
          {resultado.dry_run ? '🔍 Simulação — sem alterações' : '✓ Deduplicação concluída'}
        </div>
        {[
          { label: 'Da planilha', value: resultado.total_planilha, color: colors.textMuted },
          { label: resultado.dry_run ? 'Seriam mesclados' : 'Mesclados', value: resultado.merged, color: colors.success },
          { label: 'Órfãos (sem par)', value: resultado.orphans, color: colors.warning },
        ].map(s => (
          <div key={s.label} style={{ marginRight: 24, display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</span>
            <span style={{ fontSize: 11, color: colors.textMuted }}>{s.label}</span>
          </div>
        ))}
        <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: colors.textMuted, fontSize: 18, lineHeight: 1 }}>✕</button>
      </div>

      {/* Expandíveis */}
      <div style={{ borderTop: `1px solid ${colors.border}` }}>
        {/* Mesclados */}
        <button onClick={() => setShowMesclados(s => !s)} style={{
          width: '100%', textAlign: 'left', padding: '8px 16px', background: 'none', border: 'none',
          cursor: 'pointer', fontSize: 12, color: colors.textMuted, display: 'flex', justifyContent: 'space-between',
        }}>
          <span>📋 {mesclados.length} pares {resultado.dry_run ? 'seriam mesclados' : 'mesclados'}</span>
          <span>{showMesclados ? '▲' : '▼'}</span>
        </button>
        {showMesclados && (
          <div style={{ maxHeight: 280, overflowY: 'auto', padding: '0 16px 12px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ color: colors.textMuted }}>
                  <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: `1px solid ${colors.border}` }}>Deletado (planilha)</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: `1px solid ${colors.border}` }}>Mantido (Manager)</th>
                </tr>
              </thead>
              <tbody>
                {mesclados.map((m, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${colors.border}20`, background: m.cross_titular ? '#f9731608' : undefined }}>
                    <td style={{ padding: '4px 8px', color: colors.danger }}>{m.deletado.nome}</td>
                    <td style={{ padding: '4px 8px', color: colors.success }}>
                      {m.mantido.nome}
                      {m.cross_titular && (
                        <span style={{ marginLeft: 6, fontSize: 10, background: '#f9731622', color: '#f97316', border: '1px solid #f9731644', borderRadius: 3, padding: '1px 5px', fontWeight: 700 }}>
                          titular diferente
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Órfãos */}
        {orfaos.length > 0 && (
          <>
            <button onClick={() => setShowOrfaos(s => !s)} style={{
              width: '100%', textAlign: 'left', padding: '8px 16px', background: 'none',
              borderTop: `1px solid ${colors.border}`, borderBottom: 'none', borderLeft: 'none', borderRight: 'none',
              cursor: 'pointer', fontSize: 12, color: colors.warning, display: 'flex', justifyContent: 'space-between',
            }}>
              <span>⚠ {orfaos.length} órfãos sem par no Manager (ficam intocados)</span>
              <span>{showOrfaos ? '▲' : '▼'}</span>
            </button>
            {showOrfaos && (
              <div style={{ maxHeight: 200, overflowY: 'auto', padding: '0 16px 12px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ color: colors.textMuted }}>
                      <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: `1px solid ${colors.border}` }}>Cartão (planilha)</th>
                      <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: `1px solid ${colors.border}` }}>Motivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orfaos.map((o, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${colors.border}20` }}>
                        <td style={{ padding: '4px 8px', color: colors.warning }}>{o.nome}</td>
                        <td style={{ padding: '4px 8px', color: colors.textDim }}>{o.motivo}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Centro padrão do CARTÃO no fechamento (06/10/2026). Espelha
// CENTROS_PADRAO_CARTAO de server/lib/centro-fechamento.js — EXCLUIDO fica de
// fora, porque marca linha que não é despesa, não um cartão inteiro.
const CENTROS_PADRAO = [
  ['PESSOAL', 'Pessoal (não entra em operação)'],
  ['PESSOAL_X', 'Pessoal X'], ['PESSOAL_J', 'Pessoal J'],
  ['OPERACAO_X', 'Operação X'], ['OPERACAO_J', 'Operação J'],
  ['OPERACAO_AGENCIA', 'Operação Agência'], ['TECNOLOGIA_XMTECH24', 'Tecnologia · XMTech24'],
  ['COMPRAS_X', 'Compras X'], ['COMPRAS_J', 'Compras J'],
  ['TAXA_EMBARQUE_X', 'Taxa de embarque X'], ['TAXA_EMBARQUE_J', 'Taxa de embarque J'],
];
// Nome curto pro selo da lista: a explicação entre parênteses é da opção do
// formulário — na coluna estreita ela quebrava em quatro linhas.
// `cad` = centros cadastrados (Operação Agência...), de /api/centros-fechamento.
const rotuloCentroPadrao = (v, cad = []) => {
  const doCadastro = cad.find(c => c.codigo === v);
  if (doCadastro) return doCadastro.rotulo;
  return (CENTROS_PADRAO.find(([c]) => c === v) || [v, v])[1].replace(/\s*\(.*\)$/, '');
};

export default function CartoesPage() {
  // Investidor (perfil Titular) — null pra gestores. Trava o cadastro no
  // próprio titular; o backend também força, não confia só na tela.
  // titularRestrito aqui governa o formulário de cadastro: mesmo enxergando a
  // operação inteira, o investidor cadastra cartão no PRÓPRIO titular (o
  // servidor impõe o mesmo — ver titularRestritoDe em routes/cartoes.js).
  const titularRestrito = getTitularId();
  const [cartoes, setCartoes] = useState([]);
  const [titulares, setTitulares] = useState([]);
  const [bancos, setBancos] = useState([]);
  // Classificação do cartão: origem (a operação dona) e categoria do plano.
  const [categorias, setCategorias] = useState([]);
  const [origens, setOrigens] = useState([]);
  const [loading, setLoading] = useState(true);
  // status default '' = "Todos Status": Cadastro de Cartões é registro, mostra
  // todos por padrão (chamado #32 — SUSPENSO/INATIVO sumiam no filtro 'ativo').
  const [filtros, setFiltros] = useState({ status: '', titular_id: '', banco_id: '', tipo_titular: '', tag_local: '', origem: '', categoria_id: '', search: '' });
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  // Fora do `form` de propósito: o PUT do cartão também é usado pelo
  // investidor, e centro padrão é decisão da gestão — vai por rota própria.
  const [centroPadrao, setCentroPadrao] = useState('');
  const [centroPadraoOrig, setCentroPadraoOrig] = useState('');
  // Centros cadastrados pelo gestor (06/10/2026 — ex.: Operação Agência, padrão
  // dos cartões da Andressa). Só a gestão consulta: a rota é dela.
  const [centrosCad, setCentrosCad] = useState([]);
  useEffect(() => {
    if (!podeGerenciarCartoes()) return;
    fetch(`${API}/centros-fechamento?todos=1`, { headers: authHeaders() })
      .then(r => (r.ok ? r.json() : []))
      .then(d => setCentrosCad(Array.isArray(d) ? d.filter(c => !c.sistema) : []))
      .catch(() => setCentrosCad([]));
  }, []);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  // Aba "Excluídos": soft delete. O servidor só devolve excluído com
  // ?excluidos=1, e aí devolve SÓ eles — é daqui que o ADMIN restaura.
  const [verExcluidos, setVerExcluidos] = useState(false);
  const [activeTab, setActiveTab] = useState('basico');

  const load = useCallback(() => {
    const params = new URLSearchParams();
    // status, tipo_titular e tag_local são filtrados client-side
    const clientSideOnly = new Set(['status', 'tipo_titular', 'tag_local']);
    Object.entries(filtros).forEach(([k, v]) => { if (v && !clientSideOnly.has(k)) params.set(k, v); });
    // O servidor esconde cartão inativo/cancelado por padrão. Como o filtro de
    // status é client-side, escolher "Inativo" precisa PEDIR esses cartões ao
    // servidor — sem isto a opção existiria na tela e não mostraria nada.
    if (filtros.status) params.set('incluir_inativos', 'true');
    if (verExcluidos) params.set('excluidos', '1');
    fetch(`${API}/cartoes?${params}`, { headers: authHeaders() }).then(r => r.json()).then(setCartoes).catch(console.error).finally(() => setLoading(false));
  }, [filtros, verExcluidos]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // Mantemos a lista COMPLETA aqui (sem filtrar total_cartoes>0) pra ter o
    // tipo de qualquer titular referenciado pelos cartões (inclusive os de
    // cartões em outras páginas/filtros). O dropdown filtra por presença abaixo.
    // com_cartao_ativo: o seletor lista só quem tem cartão ativo. Sem isso ele
    // trazia os 47 titulares, 28 deles sem nenhum cartão ativo — nomes que,
    // selecionados, não filtram nada.
    fetch(`${API}/titulares?com_cartao_ativo=true`, { headers: authHeaders() }).then(r => r.json()).then(d => setTitulares(d || [])).catch(() => {});
    fetch(`${API}/bancos`).then(r => r.json()).then(setBancos).catch(() => setBancos([]));
    fetch(`${API}/centros-custo/categorias`, { headers: authHeaders() })
      .then(r => r.json()).then(d => setCategorias(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API}/centros-custo/origens`, { headers: authHeaders() })
      .then(r => r.json()).then(d => setOrigens(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  // titular_id → tipo, pra filtrar cartões por tipo do titular client-side
  const tipoPorTitularId = React.useMemo(() => {
    const m = new Map();
    for (const t of titulares) m.set(t.id, t.tipo || 'INVESTIDOR');
    return m;
  }, [titulares]);

  // Investidor: já abre com o próprio titular preenchido (e o campo fica travado).
  // O disponível digitado só vira "manual" se o campo foi mexido (02/11/2026).
  const [dispTocado, setDispTocado] = useState(false);
  const openCreate = () => {
    setDispTocado(false);
    setEditId(null);
    setForm(titularRestrito ? { ...emptyForm, titular_id: String(titularRestrito) } : emptyForm);
    setCentroPadrao(''); setCentroPadraoOrig('');
    setActiveTab('basico');
    setModalOpen(true);
  };
  const openEdit = (c) => {
    setDispTocado(false);
    setEditId(c.id);
    const f = {};
    Object.keys(emptyForm).forEach(k => { f[k] = c[k] != null ? String(c[k]) : emptyForm[k]; });
    setForm(f);
    setCentroPadrao(c.centro_padrao || ''); setCentroPadraoOrig(c.centro_padrao || '');
    setActiveTab('basico');
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.nome_cartao.trim()) return alert('Nome do cartão é obrigatório');
    if (!form.titular_id) return alert('Titular é obrigatório');
    setSaving(true);
    try {
      const method = editId ? 'PUT' : 'POST';
      const url = editId ? `${API}/cartoes/${editId}` : `${API}/cartoes`;
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(editId ? { ...form, disponivel_informado_tocado: dispTocado } : form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Erro ${res.status}`);
      }
      // Feedback de sync de limite com o Manager (só quando houve alteração)
      const data = await res.json().catch(() => ({}));
      if (data.limite_alterado) {
        if (data.manager_limite_synced) {
          alert('✓ Salvo. Novo limite também foi propagado para o Manager.');
        } else if (data.manager_limite_error) {
          alert(`✓ Salvo localmente, mas o Manager recusou a alteração de limite:\n${data.manager_limite_error}\n\nO próximo sync pode sobrescrever o limite local.`);
        }
        // Sem xmiles_id → cartão puramente local, sem sync esperado, sem alerta
      }
      if (editId && podeGerenciarCartoes() && centroPadrao !== centroPadraoOrig) {
        const r = await fetch(`${API}/cartoes/${editId}/centro-padrao`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ centro: centroPadrao || null }),
        });
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          alert(`Cartão salvo, mas o centro padrão não: ${d.error || `HTTP ${r.status}`}`);
        }
      }
      setModalOpen(false);
      load();
    } catch (e) { alert('Erro ao salvar: ' + e.message); }
    setSaving(false);
  };

  const meuTitularId = (() => {
    try { return JSON.parse(localStorage.getItem('xm_usuario') || '{}').titular_id || null; } catch { return null; }
  })();

  const criarParaConectar = async () => {
    if (!form.nome_cartao.trim()) { alert('Nome do cartão é obrigatório'); return null; }
    if (!form.titular_id) { alert('Titular é obrigatório'); return null; }
    const res = await fetch(`${API}/cartoes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(form),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Erro ${res.status}`);
    setEditId(data.id);
    load();
    return data.id;
  };

  const handleDelete = async () => {
    try {
      const res = await fetch(`${API}/cartoes/${deleteId}`, { method: 'DELETE' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Erro ${res.status}`);
      }
      setDeleteId(null);
      load();
    } catch (e) { alert('Erro ao excluir: ' + e.message); }
  };

  const handleRestaurar = async (cartao) => {
    if (!window.confirm(`Restaurar o cartão "${cartao.nome_cartao}"? Ele volta ao cadastro e ao XM Produção.`)) return;
    try {
      const res = await fetch(`${API}/cartoes/${cartao.id}/restaurar`, { method: 'POST' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Erro ${res.status}`);
      }
      load();
    } catch (e) { alert('Erro ao restaurar: ' + e.message); }
  };

  const upd = (field, val) => setForm(f => ({ ...f, [field]: val }));

  // --- Seleção em massa ---
  const [modoSelecao, setModoSelecao] = useState(false);
  const [selecionados, setSelecionados] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkMsg, setBulkMsg] = useState('');
  const [bulkStatusSel, setBulkStatusSel] = useState('SUSPENSO');
  const [bulkTagSel, setBulkTagSel] = useState(TAGS_LOCAIS[0]?.value || '');
  const [mergeModal, setMergeModal] = useState(false);
  const [antecipadoCartao, setAntecipadoCartao] = useState(null);
  const abrirMergeModal = () => { if (selecionados.size === 2) setMergeModal(true); };
  const [dedupLoading, setDedupLoading] = useState(false);
  const [dedupResultado, setDedupResultado] = useState(null);

  const executarDedup = async (dryRun, deleteOrphans = false) => {
    setDedupLoading(true);
    try {
      const r = await fetch(`${API}/cartoes/deduplicar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ dry_run: dryRun, delete_orphans: deleteOrphans }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Erro');
      setDedupResultado({ ...data, dry_run: dryRun });
      if (!dryRun) load();
    } catch (e) { alert('Erro: ' + e.message); }
    setDedupLoading(false);
  };

  const toggleSelecionado = (id) => setSelecionados(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  // Inativa/reativa um cartão não utilizado (chamado #16). Cartões INATIVO somem
  // dos "pendentes" (painel de faturas só lista ATIVO/ACTIVE), mas continuam aqui
  // na gestão. Reversível — não apaga nada (diferente do Excluir).
  const toggleInativo = async (cartao) => {
    const inativo = cartao.status === 'INATIVO';
    const novoStatus = inativo ? 'ATIVO' : 'INATIVO';
    if (!inativo && !window.confirm(`Inativar o cartão "${cartao.nome_cartao}"?\n\nEle sai dos pendentes/faturas, mas continua na gestão. Você pode reativar depois.`)) return;
    try {
      const r = await fetch(`${API}/cartoes/bulk-status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ cartao_ids: [cartao.id], status: novoStatus }),
      });
      if (!r.ok) throw new Error((await r.json()).error || 'Falha');
      load();
    } catch (e) {
      alert(`Erro ao ${inativo ? 'reativar' : 'inativar'} cartão: ${e.message}`);
    }
  };

  // Encerrar o uso: o cartão para de receber compras novas, mas continua na
  // apuração da remuneração enquanto houver saldo a amortizar. É o que a
  // Cláusula 5.4 do contrato descreve — e é diferente de inativar, que tira
  // o cartão da apuração na hora e deixaria o investidor sem receber sobre um
  // saldo que ele ainda está financiando.
  const toggleUso = async (cartao) => {
    const encerrado = !!cartao.uso_encerrado_em;
    let motivo = null;
    if (!encerrado) {
      motivo = window.prompt(
        `Encerrar o uso de "${cartao.nome_cartao}"?\n\n`
        + 'A partir de agora ele não recebe compras novas. Ele continua aparecendo na remuneração '
        + 'enquanto houver saldo a amortizar, e sai sozinho quando a fatura zerar.\n\n'
        + 'Motivo (opcional):', '');
      if (motivo === null) return;
    } else if (!window.confirm(`Voltar a usar "${cartao.nome_cartao}"? Ele passa a receber compras novas de novo.`)) {
      return;
    }
    try {
      const r = await fetch(`${API}/cartoes/${cartao.id}/${encerrado ? 'reabrir-uso' : 'encerrar-uso'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ motivo }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Falha');
      if (d.aviso) alert(d.aviso);
      load();
    } catch (e) {
      alert(`Erro ao ${encerrado ? 'reabrir' : 'encerrar'} o uso: ${e.message}`);
    }
  };

  // Liga/desliga liberação do cartão para o XM Produção lançar despesas.
  // Atualização otimista: vira o switch já, e reverte se a API falhar.
  const toggleLiberacaoXM = async (cartao) => {
    const novo = !(cartao.liberado_xm_producao !== false);
    setCartoes(prev => prev.map(c => c.id === cartao.id ? { ...c, liberado_xm_producao: novo } : c));
    try {
      const r = await fetch(`${API}/cartoes/${cartao.id}/liberacao-xm-producao`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ liberado: novo }),
      });
      if (!r.ok) throw new Error((await r.json()).error || 'Falha');
    } catch (e) {
      // reverte
      setCartoes(prev => prev.map(c => c.id === cartao.id ? { ...c, liberado_xm_producao: !novo } : c));
      alert(`Erro ao alterar liberação XM Produção: ${e.message}`);
    }
  };
  const selecionarTodos = () => setSelecionados(new Set(sorted.map(c => c.id)));
  const limparSelecao = () => { setSelecionados(new Set()); setModoSelecao(false); setBulkMsg(''); };

  const aplicarNegociacao = async (acao) => {
    if (selecionados.size === 0) return;
    setBulkLoading(true);
    setBulkMsg('');
    try {
      const r = await fetch(`${API}/cartoes/negociacao`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ cartao_ids: [...selecionados], acao }),
      });
      const data = await r.json();
      if (r.ok) {
        const msg = acao === 'ativar'
          ? `✓ ${data.atualizados} cartão(s) movidos para Negociação. ${data.transacoes_recategorizadas} lançamento(s) recategorizados.`
          : `✓ ${data.atualizados} cartão(s) removidos da Negociação.`;
        setBulkMsg(msg);
        limparSelecao();
        load();
      } else {
        setBulkMsg(`Erro: ${data.error}`);
      }
    } catch (e) {
      setBulkMsg(`Erro: ${e.message}`);
    }
    setBulkLoading(false);
  };

  const aplicarTags = async (acao) => {
    if (selecionados.size === 0 || !bulkTagSel) return;
    setBulkLoading(true);
    setBulkMsg('');
    try {
      const r = await fetch(`${API}/cartoes/bulk-tags-locais`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ cartao_ids: [...selecionados], acao, tags: [bulkTagSel] }),
      });
      const data = await r.json();
      if (r.ok) {
        const def = TAG_BY_VALUE[bulkTagSel];
        setBulkMsg(`✓ Tag "${def?.label || bulkTagSel}" ${acao === 'add' ? 'aplicada em' : 'removida de'} ${data.atualizados} cartão(s).`);
        limparSelecao();
        load();
      } else {
        setBulkMsg(`✗ ${data.error || 'Erro'}`);
      }
    } catch (e) { setBulkMsg(`✗ ${e.message}`); }
    setBulkLoading(false);
  };

  const aplicarStatus = async (novoStatus) => {
    if (selecionados.size === 0 || !novoStatus) return;
    setBulkLoading(true);
    setBulkMsg('');
    try {
      const r = await fetch(`${API}/cartoes/bulk-status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ cartao_ids: [...selecionados], status: novoStatus }),
      });
      const data = await r.json();
      if (r.ok) {
        setBulkMsg(`✓ ${data.atualizados} cartão(s) marcados como ${statusLabel(novoStatus)}.`);
        limparSelecao();
        load();
      } else {
        setBulkMsg(`Erro: ${data.error}`);
      }
    } catch (e) {
      setBulkMsg(`Erro: ${e.message}`);
    }
    setBulkLoading(false);
  };

  // Ordena por uso REAL (compras lançadas) — antes era por `fatura_atual`, que
  // está estático em 0 na maioria dos cartões e deixava a lista sem critério.
  // Copia pro cadastro o limite que o banco informou pelo Open Finance (02/11/2026).
  const usarLimiteDoBanco = async (c) => {
    if (!window.confirm(`Gravar no cadastro de ${c.nome_cartao} o limite informado pelo banco (${fmt(c.limite_efetivo)})? `
      + `Hoje o cadastro tem ${fmt(c.limite_cadastro)}.`)) return;
    try {
      const r = await fetch(`${API}/cartoes/${c.id}/limite-do-open-finance`, { method: 'POST', headers: authHeaders() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `Erro ${r.status}`);
      load();
    } catch (e) { alert('Não foi possível atualizar o limite: ' + e.message); }
  };

  // Lê agora, na Pluggy, o limite de todos os cartões conectados (02/11/2026).
  // Não pede coleta nova ao banco — não esbarra no limite de 1 por hora.
  const [limitesOf, setLimitesOf] = useState({ ocupado: false, msg: '', erro: false });
  const atualizarLimitesOf = async () => {
    setLimitesOf({ ocupado: true, msg: '', erro: false });
    try {
      const r = await fetch(`${API}/open-finance/limites`, { method: 'POST', headers: authHeaders() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `Erro ${r.status}`);
      const falhas = (d.falhas || []).length;
      setLimitesOf({ ocupado: false, erro: falhas > 0 && !d.atualizados,
        msg: `Limite do banco atualizado em ${d.atualizados} de ${d.cartoes} cartões conectados`
          + (falhas ? ` · ${falhas} conexão(ões) não responderam` : '') + '.' });
      load();
    } catch (e) { setLimitesOf({ ocupado: false, msg: 'Não foi possível atualizar os limites: ' + e.message, erro: true }); }
  };

  const { sorted: sortedAll, sortKey, sortDir, toggleSort } = useSort(cartoes, 'utilizado_efetivo', 'desc');
  const sortState = { sortKey, sortDir };

  // Filtros client-side (EN + PT agrupados + tipo do titular).
  // Cartões em negociação (gerenciamento_crise='SIM') são tratados como pseudo-status:
  // saem do filtro "Ativo" e aparecem só no filtro "Em Negociação".
  const sorted = sortedAll.filter(c => {
    if (filtros.status) {
      const emNegociacao = c.gerenciamento_crise === 'SIM';
      if (filtros.status === 'negociacao') { if (!emNegociacao) return false; }
      else {
        if (filtros.status === 'ativo' && emNegociacao) return false;
        const grupo = STATUSES_FILTRO.find(g => g.value === filtros.status);
        if (grupo && !grupo.values.includes((c.status || '').toUpperCase())) return false;
      }
    }
    if (filtros.tipo_titular) {
      const tipo = tipoPorTitularId.get(c.titular_id) || 'INVESTIDOR';
      if (tipo !== filtros.tipo_titular) return false;
    }
    if (filtros.tag_local) {
      const tags = Array.isArray(c.tags_locais) ? c.tags_locais : [];
      if (!tags.includes(filtros.tag_local)) return false;
    }
    return true;
  });

  // Titular dropdown: só mostra titulares com cartões no filtro corrente
  const titularIdsNoFiltro = new Set(sorted.map(c => c.titular_id).filter(Boolean));
  const titularesNoFiltro = titulares.filter(t => titularIdsNoFiltro.has(t.id));

  // Só ADMIN pode mexer na liberação XM Produção (backend também exige ADMIN).
  const admin = isAdmin();
  const mobile = useIsMobile();
  // Ações de gestão (dedup, remover planilha, seleção em massa) — Titular não vê.
  const gestao = podeGerenciarCartoes();

  if (loading) return <LoadingSpinner />;

  // Tab styling
  const tabStyle = (isActive) => ({
    padding: '8px 16px', border: 'none', borderBottom: isActive ? `2px solid ${colors.primary}` : '2px solid transparent',
    background: 'none', color: isActive ? colors.primary : colors.textMuted, cursor: 'pointer',
    fontSize: 13, fontWeight: isActive ? 600 : 400, transition: 'all 0.2s', whiteSpace: 'nowrap',
  });

  const renderTabContent = () => {
    const grid = { display: 'grid', gridTemplateColumns: mobile ? 'minmax(0, 1fr)' : '1fr 1fr', gap: mobile ? 0 : 16 };
    // Operação e Categoria moram na aba Classificação; quem não é ADMIN não vê
    // a aba e edita os dois em Dados Básicos.
    const operacaoECategoria = (
      <>
        <FormField label="Operação" title="A que operação este cartão serve. XMILES é o padrão; use MARCUS, IVOEI etc. para cartões que não são da XMiles.">
          <Select value={form.origem} onChange={e => upd('origem', e.target.value)}>
            {/* A lista vem do que existe, e XMILES entra sempre — no banco
                vazio ela ainda não apareceria em /origens. */}
            {[...new Set(['XMILES', ...origens.map(o => o.origem)])].map(o =>
              <option key={o} value={o}>{o}</option>)}
          </Select>
        </FormField>
        <FormField label="Categoria" title="Categoria e subcategoria do plano de contas. As de origem XMILES vêm do TechMiles e são mantidas por lá.">
          <Select value={form.categoria_id} onChange={e => upd('categoria_id', e.target.value)}>
            <option value="">— sem categoria —</option>
            {categorias
              .filter(c => !form.origem || c.origem === form.origem)
              .map(c => <option key={c.id} value={c.id}>{c.grupo ? `${c.grupo} / ${c.nome}` : c.nome}</option>)}
          </Select>
        </FormField>
      </>
    );

    switch (activeTab) {
      case 'basico':
        return (
          <>
            <div style={grid}>
              {/* Convenção da casa: TITULAR + BANCO + MODELO. É por este nome
                  que o cartão aparece em conciliação, fatura e cofre — nome
                  solto deixa dois cartões do mesmo banco indistinguíveis. */}
              <FormField label="Nome do Cartão" required>
                <Input value={form.nome_cartao} onChange={e => upd('nome_cartao', e.target.value)} placeholder="MURILO BRADESCO AMEX PLATINUM" />
                <div style={{ fontSize: 11, color: colors.textDim, marginTop: 3 }}>Padrão: titular + banco + modelo.</div>
              </FormField>
              <FormField label="Titular" required>
                {/* Investidor cadastra só no próprio titular — campo travado. */}
                <Select value={form.titular_id} disabled={!!titularRestrito}
                  onChange={e => upd('titular_id', e.target.value)}>
                  <option value="">Selecione...</option>
                  {(titularRestrito ? titulares.filter(t => t.id === titularRestrito) : titulares)
                    .map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
                </Select>
              </FormField>
              <FormField label="Banco">
                <Select value={form.banco_id} onChange={e => upd('banco_id', e.target.value)}>
                  <option value="">Selecione...</option>
                  {bancos.map(b => <option key={b.id} value={b.id}>{b.nome}</option>)}
                </Select>
              </FormField>
              <FormField label="Tipo">
                <Select value={form.tipo_cartao} onChange={e => upd('tipo_cartao', e.target.value)}>
                  {TIPOS_CARTAO.map(t => <option key={t} value={t}>{t}</option>)}
                </Select>
              </FormField>
              <FormField label="Bandeira">
                <Input value={form.bandeira} onChange={e => upd('bandeira', e.target.value)} placeholder="Visa, Master, Elo..." />
              </FormField>
              <FormField label="Final do Cartão">
                <Input value={form.numeracao_final} onChange={e => upd('numeracao_final', e.target.value)} placeholder="1234" maxLength={4} />
              </FormField>
              <FormField label="Limite Total">
                <Input type="number" value={form.limite_total} onChange={e => upd('limite_total', e.target.value)} placeholder="0.00" />
              </FormField>
              <FormField label="Limite Disponível (manual)">
                <Input type="number" value={form.limite_disponivel} placeholder="0.00"
                  onChange={e => { setDispTocado(true); upd('limite_disponivel', e.target.value); }} />
                <OrigemDoDisponivel cartao={editId ? cartoes.find(c => c.id === editId) : null} tocado={dispTocado} />
              </FormField>
              <FormField label="Fatura Atual">
                <Input type="number" value={form.fatura_atual} onChange={e => upd('fatura_atual', e.target.value)} placeholder="0.00" />
              </FormField>
              <FormField label="Pgto Mínimo">
                <Input type="number" value={form.pgto_minimo} onChange={e => upd('pgto_minimo', e.target.value)} placeholder="0.00" />
              </FormField>
              <FormField label="Melhor Dia Compra">
                <Input type="number" value={form.melhor_dia_compra} onChange={e => upd('melhor_dia_compra', e.target.value)} min="1" max="31" />
              </FormField>
              <FormField label="Data Vencimento">
                <Input value={form.data_vencimento} onChange={e => upd('data_vencimento', e.target.value)} placeholder="DD ou YYYY-MM-DD" />
              </FormField>
              <FormField label="Data Validade">
                <Input value={form.data_validade} onChange={e => upd('data_validade', e.target.value)} placeholder="MM/AAAA" />
              </FormField>
              <FormField label="Último Pagamento">
                <Input type="date" value={form.data_ultimo_pgto} onChange={e => upd('data_ultimo_pgto', e.target.value)} />
              </FormField>
              {/* Atraso, Status Pagamento (NÃO PAGO/ACORDO), Serasa, Protesto,
                  Crise e Judicializado (03/11/2026): só o ADMIN vê — mostram
                  que há cartão nessa condição. Escondido não apaga: o form
                  segue mandando o valor que veio do cartão. */}
              {admin && (
                <FormField label="Dias em Atraso">
                  <Input type="number" value={form.dias_atraso} onChange={e => upd('dias_atraso', e.target.value)} />
                </FormField>
              )}
              {admin && (
                <FormField label="Status Pagamento">
                  <Select value={form.status_pagamento} onChange={e => upd('status_pagamento', e.target.value)}>
                    {STATUS_PAGAMENTO.map(s => <option key={s} value={s}>{s}</option>)}
                  </Select>
                </FormField>
              )}
              <FormField label="Status">
                <Select value={form.status} onChange={e => upd('status', e.target.value)}>
                  <option value="ATIVO">Ativo</option>
                  <option value="SUSPENSO">Suspenso</option>
                  <option value="BLOQUEADO">Bloqueado</option>
                  <option value="INATIVO">Inativo</option>
                  <option value="CANCELADO">Cancelado</option>
                </Select>
              </FormField>
              {admin && (
                <FormField label="Serasa">
                  <Select value={form.serasa} onChange={e => upd('serasa', e.target.value)}>
                    <option value="NÃO">NÃO</option><option value="SIM">SIM</option>
                  </Select>
                </FormField>
              )}
              {admin && (
                <FormField label="Protesto">
                  <Select value={form.protesto} onChange={e => upd('protesto', e.target.value)}>
                    <option value="NÃO">NÃO</option><option value="SIM">SIM</option>
                  </Select>
                </FormField>
              )}
              {admin && (
                <FormField label="Gerenciamento de Crise">
                  <Select value={form.gerenciamento_crise} onChange={e => upd('gerenciamento_crise', e.target.value)}>
                    <option value="NÃO">NÃO</option><option value="SIM">SIM</option>
                  </Select>
                </FormField>
              )}
              {admin && (
                <FormField label="Judicializado">
                  <Select value={form.judicializado} onChange={e => upd('judicializado', e.target.value)}>
                    <option value="NÃO">NÃO</option><option value="SIM">SIM</option>
                  </Select>
                </FormField>
              )}
              {!admin && operacaoECategoria}
              <FormField label="Classif. XMiles">
                <Input value={form.classificacao_xmiles} onChange={e => upd('classificacao_xmiles', e.target.value)} placeholder="Classificação no XMiles" />
              </FormField>
              {gestao && editId && (
                <FormField label="Centro padrão no fechamento">
                  <Select value={centroPadrao} onChange={e => setCentroPadrao(e.target.value)}>
                    <option value="">Regra automática (padrão)</option>
                    {CENTROS_PADRAO.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    {centrosCad.filter(c => c.ativo).map(c => <option key={c.codigo} value={c.codigo}>{c.rotulo}</option>)}
                    {/* padrão gravado num centro cadastrado que depois foi desativado */}
                    {centroPadrao && !CENTROS_PADRAO.some(([v]) => v === centroPadrao)
                      && !centrosCad.some(c => c.ativo && c.codigo === centroPadrao) && (
                      <option value={centroPadrao}>{rotuloCentroPadrao(centroPadrao, centrosCad)} (desativado)</option>
                    )}
                  </Select>
                  <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 4, lineHeight: 1.4 }}>
                    Pra cartão que não entra em operação (ex.: pessoal) ou que é todo de uma empresa.
                    Vale pra todo lançamento do cartão sem centro escolhido à mão — inclusive meses passados.
                  </div>
                </FormField>
              )}
            </div>
            <FormField label="Observações">
              <TextArea value={form.observacoes} onChange={e => upd('observacoes', e.target.value)} placeholder="Observações..." />
            </FormField>
          </>
        );

      case 'financeiro':
        return (
          <div style={grid}>
            <FormField label="Valor Mínimo Fatura">
              <Input type="number" value={form.valor_minimo_fatura} onChange={e => upd('valor_minimo_fatura', e.target.value)} placeholder="0.00" />
            </FormField>
            <FormField label="Taxa Juros Rotativo Mensal (%)">
              <Input type="number" step="0.01" value={form.taxa_juros_rotativo_mensal} onChange={e => upd('taxa_juros_rotativo_mensal', e.target.value)} placeholder="0.00" />
            </FormField>
            <FormField label="Taxa Juros Rotativo Anual (%)">
              <Input type="number" step="0.01" value={form.taxa_juros_rotativo_anual} onChange={e => upd('taxa_juros_rotativo_anual', e.target.value)} placeholder="0.00" />
            </FormField>
            <FormField label="Taxa Juros Parcelamento (%)">
              <Input type="number" step="0.01" value={form.taxa_juros_parcelamento} onChange={e => upd('taxa_juros_parcelamento', e.target.value)} placeholder="0.00" />
            </FormField>
            <FormField label="Valor Anuidade">
              <Input type="number" value={form.valor_anuidade} onChange={e => upd('valor_anuidade', e.target.value)} placeholder="0.00" />
            </FormField>
            <FormField label="Data Contratação">
              <Input type="date" value={form.data_contratacao} onChange={e => upd('data_contratacao', e.target.value)} />
            </FormField>
            <FormField label="Número da Conta">
              <Input value={form.numero_conta} onChange={e => upd('numero_conta', e.target.value)} placeholder="Conta corrente" />
            </FormField>
            <FormField label="Agência">
              <Input value={form.agencia} onChange={e => upd('agencia', e.target.value)} placeholder="0001" />
            </FormField>
            <FormField label="Tipo de Conta">
              <Select value={form.tipo_conta} onChange={e => upd('tipo_conta', e.target.value)}>
                <option value="">Selecione...</option>
                {TIPOS_CONTA.map(t => <option key={t} value={t}>{t}</option>)}
              </Select>
            </FormField>
            <FormField label="Programa de Pontos">
              <Input value={form.programa_pontos} onChange={e => upd('programa_pontos', e.target.value)} placeholder="Livelo, Smiles..." />
            </FormField>
            <FormField label="Pontos Acumulados">
              <Input type="number" value={form.pontos_acumulados} onChange={e => upd('pontos_acumulados', e.target.value)} placeholder="0" />
            </FormField>
          </div>
        );

      case 'crise':
        return (
          <div style={grid}>
            <FormField label="Nível de Escalação">
              <Select value={form.nivel_escalacao} onChange={e => upd('nivel_escalacao', e.target.value)}>
                <option value="0">0 - Nenhum</option>
                <option value="1">1 - SAC</option>
                <option value="2">2 - Ouvidoria</option>
                <option value="3">3 - Bacen</option>
                <option value="4">4 - Consumidor.gov</option>
                <option value="5">5 - Procon</option>
                <option value="6">6 - Reclame Aqui</option>
              </Select>
            </FormField>
            <FormField label="Rodada de Negociação">
              <Input type="number" value={form.rodada_negociacao} onChange={e => upd('rodada_negociacao', e.target.value)} min="0" />
            </FormField>
            <FormField label="Última Negociação">
              <Input type="date" value={form.data_ultima_negociacao} onChange={e => upd('data_ultima_negociacao', e.target.value)} />
            </FormField>
            <FormField label="Próxima Rodada">
              <Input type="date" value={form.data_proxima_rodada} onChange={e => upd('data_proxima_rodada', e.target.value)} />
            </FormField>
            <FormField label="Protocolo SAC">
              <Input value={form.protocolo_sac} onChange={e => upd('protocolo_sac', e.target.value)} placeholder="Número do protocolo" />
            </FormField>
            <FormField label="Protocolo Ouvidoria">
              <Input value={form.protocolo_ouvidoria} onChange={e => upd('protocolo_ouvidoria', e.target.value)} placeholder="Número do protocolo" />
            </FormField>
            <FormField label="Caso Consumidor.gov">
              <Input value={form.numero_caso_consumidor_gov} onChange={e => upd('numero_caso_consumidor_gov', e.target.value)} placeholder="Número do caso" />
            </FormField>
            <FormField label="Caso Procon">
              <Input value={form.numero_caso_procon} onChange={e => upd('numero_caso_procon', e.target.value)} placeholder="Número do caso" />
            </FormField>
            <FormField label="Status do Acordo">
              <Select value={form.status_acordo} onChange={e => upd('status_acordo', e.target.value)}>
                {STATUS_ACORDO.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
              </Select>
            </FormField>
            <FormField label="Data do Acordo">
              <Input type="date" value={form.data_acordo} onChange={e => upd('data_acordo', e.target.value)} />
            </FormField>
            <FormField label="Proposta Acordo (R$)">
              <Input type="number" value={form.proposta_acordo_valor} onChange={e => upd('proposta_acordo_valor', e.target.value)} placeholder="0.00" />
            </FormField>
            <FormField label="Desconto Proposto (%)">
              <Input type="number" step="0.1" value={form.proposta_acordo_desconto_pct} onChange={e => upd('proposta_acordo_desconto_pct', e.target.value)} placeholder="0.0" />
            </FormField>
            <FormField label="Parcelas do Acordo">
              <Input type="number" value={form.proposta_acordo_parcelas} onChange={e => upd('proposta_acordo_parcelas', e.target.value)} placeholder="0" />
            </FormField>
            <FormField label="Dívida Original (R$)">
              <Input type="number" value={form.valor_divida_original} onChange={e => upd('valor_divida_original', e.target.value)} placeholder="0.00" />
            </FormField>
            <FormField label="Dívida Atualizada (R$)">
              <Input type="number" value={form.valor_divida_atualizada} onChange={e => upd('valor_divida_atualizada', e.target.value)} placeholder="0.00" />
            </FormField>
          </div>
        );

      case 'legal':
        return (
          <div style={grid}>
            <FormField label="Processo Judicial">
              <Input value={form.processo_judicial} onChange={e => upd('processo_judicial', e.target.value)} placeholder="Número do processo" />
            </FormField>
            <FormField label="Citação Recebida">
              <Select value={form.citacao_recebida} onChange={e => upd('citacao_recebida', e.target.value)}>
                <option value="NÃO">NÃO</option><option value="SIM">SIM</option>
              </Select>
            </FormField>
            <FormField label="Data da Citação">
              <Input type="date" value={form.data_citacao} onChange={e => upd('data_citacao', e.target.value)} />
            </FormField>
            <FormField label="Advogado Responsável">
              <Input value={form.advogado_responsavel} onChange={e => upd('advogado_responsavel', e.target.value)} placeholder="Nome do advogado" />
            </FormField>
            <FormField label="DED/DDC Solicitado">
              <Select value={form.ded_ddc_solicitado} onChange={e => upd('ded_ddc_solicitado', e.target.value)}>
                <option value="NÃO">NÃO</option><option value="SIM">SIM</option>
              </Select>
            </FormField>
            <FormField label="DED/DDC Recebido">
              <Select value={form.ded_ddc_recebido} onChange={e => upd('ded_ddc_recebido', e.target.value)}>
                <option value="NÃO">NÃO</option><option value="SIM">SIM</option>
              </Select>
            </FormField>
            <FormField label="Data Solicitação DED">
              <Input type="date" value={form.data_solicitacao_ded} onChange={e => upd('data_solicitacao_ded', e.target.value)} />
            </FormField>
          </div>
        );

      case 'classificacao':
        return (
          <div style={grid}>
            <FormField label="Prioridade de Pagamento (1-5)">
              <Select value={form.prioridade_pagamento} onChange={e => upd('prioridade_pagamento', e.target.value)}>
                <option value="1">1 - Máxima</option>
                <option value="2">2 - Alta</option>
                <option value="3">3 - Média</option>
                <option value="4">4 - Baixa</option>
                <option value="5">5 - Mínima</option>
              </Select>
            </FormField>
            <FormField label="Risco (calculado automaticamente)">
              <div style={{
                padding: '8px 12px', borderRadius: 6, background: colors.bg, border: `1px solid ${colors.border}`,
                color: riscoColor(form.risco), fontWeight: 600, fontSize: 14
              }}>
                {form.risco || 'BAIXO'}
                <span style={{ fontSize: 11, color: colors.textDim, fontWeight: 400, marginLeft: 8 }}>
                  (auto: dias_atraso + serasa + protesto + fatura)
                </span>
              </div>
            </FormField>
            {operacaoECategoria}
            <FormField label="Categoria de Estratégia">
              <Input value={form.categoria_estrategia} onChange={e => upd('categoria_estrategia', e.target.value)} placeholder="Ex: Renegociação, Blindagem..." />
            </FormField>
            <FormField label="Bloqueado Desde">
              <Input type="date" value={form.bloqueado_desde} onChange={e => upd('bloqueado_desde', e.target.value)} />
            </FormField>
            <FormField label="Motivo do Bloqueio" style={{ gridColumn: '1 / -1' }}>
              <TextArea value={form.motivo_bloqueio} onChange={e => upd('motivo_bloqueio', e.target.value)} placeholder="Motivo do bloqueio do cartão..." />
            </FormField>
          </div>
        );

      case 'openfinance':
        return (
          <OpenFinanceCartao
            cartaoId={editId}
            podeConectarNoCadastro={!!meuTitularId && String(form.titular_id) === String(meuTitularId)}
            onCriarCartao={criarParaConectar}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
        <PageHeader title="Cartões de Crédito" subtitle={`${cartoes.length} cartões encontrados`}
          onAdd={openCreate} addLabel="Criar novo Cartão" />
        {gestao && <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={atualizarLimitesOf}
            disabled={limitesOf.ocupado}
            title="Lê agora o limite, o disponível e a fatura que o banco informa, em todos os cartões conectados ao Open Finance"
            style={{
              marginTop: 4, padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
              border: `1px solid ${colors.success}80`, background: `${colors.success}18`, color: colors.success,
              display: 'flex', alignItems: 'center', gap: 6, opacity: limitesOf.ocupado ? 0.5 : 1,
            }}
          >
            ↻ {limitesOf.ocupado ? 'Atualizando limites…' : 'Atualizar limites do Open Finance'}
          </button>
          <button
            onClick={() => executarDedup(true)}
            disabled={dedupLoading}
            title="Simula a deduplicação sem alterar dados — mostra quantos duplicados seriam removidos"
            style={{
              marginTop: 4, padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
              border: `1px solid ${colors.info}80`, background: `${colors.info}18`, color: colors.info,
              display: 'flex', alignItems: 'center', gap: 6, opacity: dedupLoading ? 0.5 : 1,
            }}
          >
            🔍 Simular Dedup
          </button>
          <button
            onClick={() => {
              if (!window.confirm('Remove TODOS os cartões da planilha:\n• Com par no Manager → mescla dados e deleta\n• Sem par (órfãos) → deleta também\n\nContinuar?')) return;
              executarDedup(false, true);
            }}
            disabled={dedupLoading}
            title="Remove todos os cartões da planilha — mescla os que têm par, deleta os órfãos"
            style={{
              marginTop: 4, padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
              border: `1px solid ${colors.danger}80`, background: `${colors.danger}18`, color: colors.danger,
              display: 'flex', alignItems: 'center', gap: 6, opacity: dedupLoading ? 0.5 : 1,
            }}
          >
            🗑 Remover Planilha
          </button>
          <button
            onClick={() => { setModoSelecao(!modoSelecao); setSelecionados(new Set()); setBulkMsg(''); }}
            style={{
              marginTop: 4, padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
              border: `1px solid ${modoSelecao ? colors.warning : colors.warning + '80'}`,
              background: modoSelecao ? colors.warning : colors.warning + '18',
              color: modoSelecao ? '#000' : colors.warning,
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <CheckSquare size={14} />{modoSelecao ? '✕ Cancelar seleção' : '☑ Selecionar em massa'}
          </button>
        </div>}
      </div>

      {limitesOf.msg && (
        <div style={{
          marginBottom: 12, padding: '10px 16px', borderRadius: 8, fontSize: 13, display: 'flex', justifyContent: 'space-between',
          background: `${limitesOf.erro ? colors.danger : colors.success}15`,
          border: `1px solid ${limitesOf.erro ? colors.danger : colors.success}30`,
          color: limitesOf.erro ? colors.danger : colors.success,
        }}>
          <span>{limitesOf.msg}</span>
          <button onClick={() => setLimitesOf(s => ({ ...s, msg: '' }))}
            style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>✕</button>
        </div>
      )}

      {/* Resultado da deduplicação */}
      {dedupResultado && <DedupResultado resultado={dedupResultado} onClose={() => setDedupResultado(null)} />}

      {bulkMsg && (
        <div style={{
          marginBottom: 12, padding: '10px 16px', borderRadius: 8, fontSize: 13,
          background: `${colors.success}15`, border: `1px solid ${colors.success}30`, color: colors.success,
        }}>{bulkMsg}</div>
      )}

      <SearchBar value={filtros.search} onChange={e => setFiltros(f => ({ ...f, search: e.target.value }))}
        placeholder="Buscar cartão ou titular...">
        <Select value={filtros.tipo_titular} onChange={e => setFiltros(f => ({ ...f, tipo_titular: e.target.value, titular_id: '' }))} style={{ width: 'auto', minWidth: 140 }}>
          <option value="">Todos os tipos</option>
          <option value="INVESTIDOR">Investidor</option>
          <option value="CLIENTE">Cliente</option>
        </Select>
        <Select value={filtros.titular_id} onChange={e => setFiltros(f => ({ ...f, titular_id: e.target.value }))} style={{ width: 'auto', minWidth: 170 }}>
          <option value="">Todos Titulares ({titularesNoFiltro.length})</option>
          {titularesNoFiltro.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
        </Select>
        <Select value={filtros.banco_id} onChange={e => setFiltros(f => ({ ...f, banco_id: e.target.value }))} style={{ width: 'auto', minWidth: 150 }}>
          <option value="">Todos Bancos</option>
          {bancos.map(b => <option key={b.id} value={b.id}>{b.nome}</option>)}
        </Select>
        <Select value={filtros.status} onChange={e => setFiltros(f => ({ ...f, status: e.target.value, titular_id: '' }))} style={{ width: 'auto', minWidth: 150 }}>
          <option value="">Todos Status</option>
          {/* "Em Negociação" só pra quem enxerga esses cartões — pros demais o
              servidor nem manda, então a opção só devolveria lista vazia. */}
          {STATUSES_FILTRO.filter(s => s.value !== 'negociacao' || veNegociacao())
            .map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </Select>
        {/* Seletor "Todas Fases" removido a pedido — a coluna FASE continua na
            tabela e é ordenável, então dá pra agrupar por fase sem o filtro. */}
        <Select value={filtros.origem} onChange={e => setFiltros(f => ({ ...f, origem: e.target.value }))} style={{ width: 'auto', minWidth: 140 }}>
          <option value="">Todas Operações</option>
          {origens.map(o => <option key={o.origem} value={o.origem}>{o.origem} ({o.cartoes})</option>)}
        </Select>
        <Select value={filtros.categoria_id} onChange={e => setFiltros(f => ({ ...f, categoria_id: e.target.value }))} style={{ width: 'auto', minWidth: 190 }}>
          <option value="">Todas Categorias</option>
          {/* Sem categoria vem primeiro: é por essa lista que a classificação
              dos 372 cartões vai ser feita. */}
          <option value="sem">— sem categoria —</option>
          {categorias
            .filter(c => !filtros.origem || c.origem === filtros.origem)
            .map(c => <option key={c.id} value={c.id}>{c.grupo ? `${c.grupo} / ${c.nome}` : c.nome}</option>)}
        </Select>
        <Select value={filtros.tag_local} onChange={e => setFiltros(f => ({ ...f, tag_local: e.target.value }))} style={{ width: 'auto', minWidth: 150 }}>
          <option value="">Todas Tags</option>
          {TAGS_LOCAIS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </Select>
        {gestao && (
          <Button size="sm" variant={verExcluidos ? 'solid' : 'outline'} color={colors.danger} icon={Trash2}
            onClick={() => setVerExcluidos(v => !v)}
            title="Cartões excluídos: fora do cadastro e do XM Produção, histórico preservado">
            {verExcluidos ? 'Voltar aos cartões' : 'Excluídos'}
          </Button>
        )}
      </SearchBar>

      {verExcluidos && (
        <div style={{
          marginBottom: 12, padding: '10px 16px', borderRadius: 8, fontSize: 13,
          background: `${colors.danger}10`, border: `1px solid ${colors.danger}30`, color: colors.text,
        }}>
          Cartões <b>excluídos</b>: não aparecem no cadastro nem no XM Produção, mas faturas, transações e relatórios
          continuam com o histórico. {admin ? 'Restaure pelo botão da linha.' : 'Só ADMIN restaura.'}
        </div>
      )}

      {!verExcluidos && cartoes.length > 0 && <TotaisDaLista cartoes={sorted} />}

      {cartoes.length === 0 ? (
        <EmptyState message="Nenhum cartão encontrado" icon={CreditCard} />
      ) : (
        <Table>
          <thead><tr>
            {modoSelecao && (
              <Th>
                <button onClick={selecionarTodos} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.primary, padding: 0 }} title="Selecionar todos">
                  <CheckSquare size={15} />
                </button>
              </Th>
            )}
            <Th sortKey="nome_cartao" sortState={sortState} onSort={toggleSort}>Cartão</Th>
            <Th sortKey="titular_nome" sortState={sortState} onSort={toggleSort}>Titular</Th>
            <Th sortKey="banco_nome" sortState={sortState} onSort={toggleSort}>Banco</Th>
            <Th sortKey="bandeira" sortState={sortState} onSort={toggleSort}>Bandeira</Th>
            <Th>Final</Th>
            {/* Cartão conectado ao Open Finance (02/11/2026): limite, utilizado e
                disponível são os do BANCO; o limite do cadastro vai embaixo. */}
            <Th sortKey="limite_efetivo" sortState={sortState} onSort={toggleSort}>Limite</Th>
            {/* Utilizado/Disponível vêm das COMPRAS LANÇADAS (mesma conta do
                Dashboard), não do campo cadastrado `fatura_atual`, que ficava
                estático em 0 e fazia a tela mostrar 0% de uso em tudo. */}
            <Th sortKey="utilizado_efetivo" sortState={sortState} onSort={toggleSort}>Utilizado</Th>
            <Th sortKey="disponivel_efetivo" sortState={sortState} onSort={toggleSort}>Disponível</Th>
            <Th sortKey="pct_efetivo" sortState={sortState} onSort={toggleSort}>% Uso</Th>
            {/* Atraso, Fase, Risco e Serasa: só o ADMIN (03/11/2026). */}
            {admin && <Th sortKey="dias_atraso" sortState={sortState} onSort={toggleSort}>Atraso</Th>}
            {admin && <Th sortKey="fase" sortState={sortState} onSort={toggleSort}>Fase</Th>}
            {admin && <Th sortKey="risco" sortState={sortState} onSort={toggleSort}>Risco</Th>}
            <Th sortKey="status" sortState={sortState} onSort={toggleSort}>Status</Th>
            <Th align="center">XM Prod.</Th>
            {admin && <Th>Serasa</Th>}<Th align="center">Ações</Th>
          </tr></thead>
          <tbody>
            {sorted.map(c => {
              const pct = (c.pct_efetivo ?? (c.limite_total ? (c.limite_utilizado / c.limite_total) * 100 : 0)).toFixed(0);
              const doBanco = c.limite_fonte === 'open_finance';
              const emNegociacao = c.gerenciamento_crise === 'SIM';
              const sel = selecionados.has(c.id);
              return (
                <TrHover key={c.id} style={emNegociacao ? { background: `${colors.warning}08` } : {}}>
                  {modoSelecao && (
                    <Td>
                      <button onClick={() => toggleSelecionado(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: sel ? colors.primary : colors.textDim, padding: 0 }}>
                        {sel ? <CheckSquare size={16} /> : <Square size={16} />}
                      </button>
                    </Td>
                  )}
                  <Td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600 }}>{c.nome_cartao}</span>
                      {doBanco && (
                        <span title={`Open Finance conectado: limite e disponível informados pelo banco${c.open_finance_limite_em ? ` em ${new Date(c.open_finance_limite_em).toLocaleString('pt-BR')}` : ''}`}
                          style={{
                            fontSize: 9, padding: '1px 6px', borderRadius: 8, fontWeight: 700,
                            background: `${colors.success}25`, color: colors.success, letterSpacing: '0.03em',
                          }}>OPEN FINANCE</span>
                      )}
                      {emNegociacao && (
                        <span style={{
                          fontSize: 9, padding: '1px 6px', borderRadius: 8, fontWeight: 700,
                          background: `${colors.warning}25`, color: colors.warning, letterSpacing: '0.03em',
                        }}>NEGOCIAÇÃO</span>
                      )}
                      {/* Os selos SEM BAIXA (#64) e SEM FATURA/venceu há Xd (#67)
                          saíram DESTA listagem a pedido do gestor (18/08/2026):
                          aqui viravam ruído em cima de quase todo cartão da
                          Jaguar. Os campos continuam vindo da API — a cobrança
                          de fatura faltando vive no Painel do Investidor e na
                          Conciliação, que é onde dá pra agir sobre ela. */}
                      {/* Classificação: operação dona do cartão e categoria do
                          plano. A operação só vira badge quando NÃO é XMILES —
                          marcar os 340 cartões da XMiles seria ruído. */}
                      {c.origem && String(c.origem).toUpperCase() !== 'XMILES' && (
                        <span title={`Operação: ${c.origem}`} style={{
                          fontSize: 9, padding: '1px 6px', borderRadius: 8, fontWeight: 700,
                          background: `${colors.info}25`, color: colors.info, letterSpacing: '0.03em',
                        }}>{String(c.origem).toUpperCase()}</span>
                      )}
                      {c.categoria_nome && (
                        <span title={`Categoria: ${c.categoria_grupo ? c.categoria_grupo + ' / ' : ''}${c.categoria_nome}`}
                          style={{
                            fontSize: 9, padding: '1px 6px', borderRadius: 8, fontWeight: 600,
                            background: `${colors.textMuted}20`, color: colors.textMuted,
                            maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>{c.categoria_grupo ? `${c.categoria_grupo} / ${c.categoria_nome}` : c.categoria_nome}</span>
                      )}
                      {(c.tags_locais || []).map(tag => {
                        const def = TAG_BY_VALUE[tag];
                        if (!def) return null;
                        return (
                          <span key={tag} title={`Tag local: ${def.label} (não vai pro Manager)`} style={{
                            fontSize: 9, padding: '1px 6px', borderRadius: 8, fontWeight: 700,
                            background: `${def.color}25`, color: def.color, letterSpacing: '0.03em',
                            border: `1px dashed ${def.color}55`,
                          }}>{def.label.toUpperCase()}</span>
                        );
                      })}
                    </div>
                  </Td>
                  <Td>{c.titular_nome}</Td>
                  <Td>{c.banco_nome || '-'}</Td>
                  <Td>{c.bandeira || '-'}</Td>
                  <Td>{c.numeracao_final || '-'}</Td>
                  <Td style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                    {fmt(c.limite_efetivo ?? c.limite_total)}
                    {doBanco && (
                      <div style={{ fontSize: 10, color: colors.textMuted, fontWeight: 400, display: 'flex', alignItems: 'center', gap: 4 }}
                        title="Limite do cadastro">
                        cadastro {fmt(c.limite_cadastro)}
                        {Math.abs((c.limite_cadastro || 0) - (c.limite_efetivo || 0)) >= 0.01 && (
                          <button onClick={() => usarLimiteDoBanco(c)} title="Gravar no cadastro o limite informado pelo banco"
                            style={{ background: 'none', border: 'none', color: colors.primary, cursor: 'pointer', padding: 0, display: 'flex' }}>
                            <RefreshCw size={11} />
                          </button>
                        )}
                      </div>
                    )}
                  </Td>
                  <Td style={{ fontWeight: 600, color: colors.danger }} title={explicarUtilizado(c)}>{fmt(c.utilizado_efetivo ?? c.limite_utilizado)}</Td>
                  <Td style={{ fontWeight: 600, color: colors.success, whiteSpace: 'nowrap' }} title={explicarUtilizado(c)}>
                    {fmt(c.disponivel_efetivo ?? c.limite_disponivel_real)}
                    {c.limite_fonte === 'informado' && (
                      <div style={{ fontSize: 10, color: colors.warning, fontWeight: 400 }}>
                        manual em {new Date(c.limite_atualizado_em).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit' })}
                      </div>
                    )}
                  </Td>
                  <Td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ background: colors.bg, borderRadius: 4, overflow: 'hidden', height: 6, width: 50 }}>
                        <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: pct > 90 ? colors.danger : pct > 70 ? colors.warning : colors.success, borderRadius: 4 }} />
                      </div>
                      <span style={{ fontSize: 11, color: colors.textMuted }}>{pct}%</span>
                    </div>
                  </Td>
                  {admin && (
                    <Td style={{ color: c.dias_atraso > 0 ? colors.danger : colors.success, fontWeight: 600 }}>
                      {c.dias_atraso > 0 ? `${c.dias_atraso}d` : '-'}
                    </Td>
                  )}
                  {admin && <Td><Badge text={c.fase || 'N/A'} color={faseColor(c.fase)} /></Td>}
                  {admin && <Td><Badge text={c.risco || 'BAIXO'} color={riscoColor(c.risco)} /></Td>}
                  <Td>
                    <Badge text={statusLabel(c.status)} color={statusColorFromClass(statusCorClass(c.status))} />
                    {/* Uso encerrado é o estado do meio: não recebe compra
                        nova, mas segue remunerando enquanto amortiza. Sem
                        mostrar aqui, o cartão parece ativo e confunde. */}
                    {/* Só gestão: centro de fechamento é conceito interno, e o
                        investidor também vê esta lista. */}
                    {gestao && c.centro_padrao && (
                      <div style={{ fontSize: 10, color: colors.info, marginTop: 3, whiteSpace: 'nowrap' }}
                        title="Centro padrão no fechamento: vale pra todo lançamento deste cartão sem centro escolhido à mão">
                        centro: {rotuloCentroPadrao(c.centro_padrao, centrosCad)}
                      </div>
                    )}
                    {c.uso_encerrado_em && (
                      <div style={{ fontSize: 10, color: colors.warning, marginTop: 3 }}
                        title={c.uso_encerrado_motivo || 'Uso encerrado: não recebe compras novas, mas segue na remuneração enquanto amortiza'}>
                        uso encerrado {String(c.uso_encerrado_em).slice(0, 10).split('-').reverse().join('/')}
                      </div>
                    )}
                  </Td>
                  <Td style={{ textAlign: 'center' }}>
                    {(() => {
                      // Indisponível por regra de negócio (negociação/judicial/status/uso encerrado) → não dá pra ligar
                      const bloqueadoPorRegra = emNegociacao || c.judicializado === 'SIM' || c.status !== 'ATIVO' || !!c.uso_encerrado_em;
                      const ligado = c.liberado_xm_producao !== false;
                      const disponivel = ligado && !bloqueadoPorRegra;
                      // Não-admin vê o estado mas não pode alterar (backend exige ADMIN).
                      const desabilitado = bloqueadoPorRegra || !admin;
                      const titulo = bloqueadoPorRegra
                        ? ((emNegociacao || c.judicializado === 'SIM') && !admin ? 'Indisponível para XM Produção'
                            : emNegociacao ? 'Em negociação — indisponível para XM Produção'
                            : c.judicializado === 'SIM' ? 'Judicializado — indisponível'
                            : c.uso_encerrado_em ? 'Uso encerrado — não recebe compras novas (a remuneração segue enquanto amortiza)'
                            : `Status ${c.status} — indisponível`)
                        : !admin
                          ? `${ligado ? 'Liberado' : 'Desligado'} para XM Produção (só ADMIN altera)`
                          : (ligado ? 'Liberado para XM Produção (clique para desligar)'
                                    : 'Desligado para XM Produção (clique para ligar)');
                      return (
                        <button
                          onClick={() => !desabilitado && toggleLiberacaoXM(c)}
                          title={titulo}
                          disabled={desabilitado}
                          style={{
                            width: 38, height: 22, borderRadius: 11, border: 'none', padding: 0,
                            position: 'relative', cursor: desabilitado ? 'not-allowed' : 'pointer',
                            background: disponivel ? colors.success : colors.border,
                            opacity: bloqueadoPorRegra ? 0.4 : (admin ? 1 : 0.6), transition: 'background 0.15s',
                          }}
                        >
                          <span style={{
                            position: 'absolute', top: 2, left: disponivel ? 18 : 2,
                            width: 18, height: 18, borderRadius: '50%', background: '#fff',
                            transition: 'left 0.15s',
                          }} />
                        </button>
                      );
                    })()}
                  </Td>
                  {admin && (
                    <Td style={{ textAlign: 'center' }}>
                      {c.serasa === 'SIM' ? <span style={{ color: colors.danger, fontWeight: 700 }}>SIM</span> : <span style={{ color: colors.textDim }}>Não</span>}
                    </Td>
                  )}
                  <Td>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <button
                        onClick={() => toggleInativo(c)}
                        title={c.status === 'INATIVO' ? 'Reativar cartão' : 'Inativar cartão (some dos pendentes)'}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                          display: 'inline-flex', borderRadius: 6,
                          color: c.status === 'INATIVO' ? colors.success : colors.textDim,
                        }}>
                        {c.status === 'INATIVO' ? <Power size={14} /> : <PowerOff size={14} />}
                      </button>
                      {/* Encerrar uso ≠ inativar. Inativar tira da apuração na
                          hora; encerrar o uso corta só as compras novas e
                          mantém a remuneração até amortizar (Cláusula 5.4). */}
                      {c.status !== 'INATIVO' && (
                        <button
                          onClick={() => toggleUso(c)}
                          title={c.uso_encerrado_em
                            ? 'Voltar a usar este cartão'
                            : 'Encerrar o uso: para de receber compras novas, mas segue remunerando enquanto amortiza'}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                            display: 'inline-flex', borderRadius: 6,
                            color: c.uso_encerrado_em ? colors.warning : colors.textDim,
                          }}>
                          <Ban size={14} />
                        </button>
                      )}
                      {verExcluidos ? (
                        <>
                          <span style={{ fontSize: 11, color: colors.textDim }}>
                            {c.mesclado_em ? `mesclado no #${c.mesclado_em}` : `excluído em ${new Date(c.excluido_em).toLocaleDateString('pt-BR')}`}
                          </span>
                          {admin && !c.mesclado_em && (
                            <button onClick={() => handleRestaurar(c)} title="Restaurar cartão"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'inline-flex', borderRadius: 6, color: colors.success }}>
                              <RotateCcw size={14} />
                            </button>
                          )}
                        </>
                      ) : (
                        <>
                          {gestao && (
                            <button onClick={() => setAntecipadoCartao(c)} title="Pagamento antecipado (libera limite antes de a fatura fechar)"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'inline-flex', borderRadius: 6, color: colors.success }}>
                              <Banknote size={14} />
                            </button>
                          )}
                          <ActionButtons onEdit={() => openEdit(c)} onDelete={() => setDeleteId(c.id)} />
                        </>
                      )}
                    </div>
                  </Td>
                </TrHover>
              );
            })}
          </tbody>
        </Table>
      )}

      {/* Bulk Action Bar */}
      {modoSelecao && selecionados.size > 0 && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: colors.card, border: `1px solid ${colors.border}`,
          borderRadius: 14, padding: '12px 20px', boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', gap: 10, zIndex: 500, flexWrap: 'wrap',
          maxWidth: 780,
        }}>
          {/* Contagem */}
          <span style={{ fontSize: 13, fontWeight: 700, color: colors.text, whiteSpace: 'nowrap' }}>
            <AlertOctagon size={14} style={{ marginRight: 5, color: colors.warning, verticalAlign: 'middle' }} />
            {selecionados.size} selecionado(s)
          </span>

          {/* Divisor */}
          <div style={{ width: 1, height: 28, background: colors.border }} />

          {/* Bloco Negociação — só pra quem trabalha com negociação. Quem não
              enxerga esses cartões também não os move pra lá. */}
          {veNegociacao() && (
            <>
              <button onClick={() => aplicarNegociacao('ativar')} disabled={bulkLoading}
                style={{ padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', background: colors.warning, color: '#000', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
                {bulkLoading ? '...' : '📋 Em Negociação'}
              </button>
              <button onClick={() => aplicarNegociacao('desativar')} disabled={bulkLoading}
                style={{ padding: '7px 14px', borderRadius: 8, border: `1px solid ${colors.border}`, background: 'transparent', color: colors.textMuted, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                ↩ Sair Negociação
              </button>

              {/* Divisor */}
              <div style={{ width: 1, height: 28, background: colors.border }} />
            </>
          )}

          {/* Bloco Classificar Status */}
          <span style={{ fontSize: 11, color: colors.textDim, whiteSpace: 'nowrap' }}>Classificar como:</span>
          <select
            value={bulkStatusSel}
            onChange={e => setBulkStatusSel(e.target.value)}
            disabled={bulkLoading}
            style={{
              padding: '7px 10px', borderRadius: 8, border: `1px solid ${colors.border}`,
              background: colors.bg, color: colors.text, fontSize: 12, cursor: 'pointer',
            }}
          >
            <option value="ATIVO">Ativo</option>
            <option value="SUSPENSO">Suspenso</option>
            <option value="BLOQUEADO">Bloqueado</option>
            <option value="INATIVO">Inativo</option>
            <option value="CANCELADO">Cancelado</option>
          </select>
          <button onClick={() => aplicarStatus(bulkStatusSel)} disabled={bulkLoading}
            style={{ padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', background: colors.primary, color: '#fff', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
            {bulkLoading ? '...' : '✓ Aplicar'}
          </button>

          {/* Divisor */}
          <div style={{ width: 1, height: 28, background: colors.border }} />

          {/* Bloco Tags Locais */}
          <span style={{ fontSize: 11, color: colors.textDim, whiteSpace: 'nowrap' }}>Tag local:</span>
          <select value={bulkTagSel} onChange={e => setBulkTagSel(e.target.value)} disabled={bulkLoading}
            style={{ padding: '7px 10px', borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.bg, color: colors.text, fontSize: 12, cursor: 'pointer' }}>
            {TAGS_LOCAIS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <button onClick={() => aplicarTags('add')} disabled={bulkLoading}
            title="Adiciona a tag aos cartões selecionados (não vai pro Manager)"
            style={{ padding: '7px 12px', borderRadius: 8, border: `1px solid ${colors.info}66`, background: `${colors.info}22`, color: colors.info, fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            + Tag
          </button>
          <button onClick={() => aplicarTags('remove')} disabled={bulkLoading}
            title="Remove a tag dos cartões selecionados"
            style={{ padding: '7px 12px', borderRadius: 8, border: `1px solid ${colors.border}`, background: 'transparent', color: colors.textMuted, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            − Tag
          </button>

          {/* Divisor */}
          <div style={{ width: 1, height: 28, background: colors.border }} />

          {/* Bloco Mesclar (só quando exatamente 2 selecionados) */}
          <button
            onClick={abrirMergeModal}
            disabled={selecionados.size !== 2 || bulkLoading}
            title={selecionados.size !== 2 ? 'Selecione exatamente 2 cartões para mesclar' : 'Mesclar 2 cartões'}
            style={{
              padding: '7px 14px', borderRadius: 8, border: 'none',
              cursor: selecionados.size === 2 ? 'pointer' : 'not-allowed',
              background: selecionados.size === 2 ? '#8b5cf6' : colors.border,
              color: selecionados.size === 2 ? '#fff' : colors.textDim,
              fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
              opacity: selecionados.size === 2 ? 1 : 0.6,
            }}>
            🔗 Mesclar 2 cartões
          </button>

          {/* Fechar */}
          <button onClick={limparSelecao} style={{ background: 'none', border: 'none', color: colors.textDim, cursor: 'pointer', padding: 4, marginLeft: 'auto' }}>
            <X size={16} />
          </button>
        </div>
      )}

      {/* Modal Merge */}
      {mergeModal && (
        <MergeModal
          cartoes={cartoes.filter(c => selecionados.has(c.id))}
          onClose={() => setMergeModal(false)}
          onMerged={() => { setMergeModal(false); limparSelecao(); load(); }}
        />
      )}

      {antecipadoCartao && (
        <PagamentoAntecipadoModal
          cartao={antecipadoCartao}
          onClose={() => setAntecipadoCartao(null)}
          onChanged={load}
        />
      )}

      {/* Create/Edit Modal with Tabs */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editId ? 'Editar Cartão' : 'Criar novo Cartão'} width={850}>
        {/* Tab navigation */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${colors.border}`, marginBottom: 16, gap: 4, overflowX: 'auto' }}>
          {TABS.filter(tab => admin || !tab.soAdmin).map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={tabStyle(activeTab === tab.id)}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {renderTabContent()}

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16, paddingTop: 16, borderTop: `1px solid ${colors.border}` }}>
          <Button variant="outline" color={colors.textMuted} onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving}>{saving ? 'Salvando...' : editId ? 'Salvar Alterações' : 'Criar Cartão'}</Button>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete}
        title="Excluir Cartão" message="O cartão sai do cadastro e some do XM Produção. Faturas, transações e relatórios continuam com o histórico. Um ADMIN pode restaurá-lo depois, na lista de Excluídos."
      />
    </div>
  );
}

// ─────────────────────────────────────
// MergeModal — mescla 2 cartões em 1
// ─────────────────────────────────────
function MergeModal({ cartoes, onClose, onMerged }) {
  const [c1, c2] = cartoes;
  const [targetId, setTargetId] = useState(c1?.id);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const sourceId = targetId === c1?.id ? c2?.id : c1?.id;

  // Carregar preview (dry_run) sempre que mudar a escolha
  useEffect(() => {
    if (!targetId || !sourceId) return;
    setPreview(null);
    fetch(`${API}/cartoes/merge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ source_id: sourceId, target_id: targetId, dry_run: true }),
    }).then(r => r.json()).then(setPreview).catch(() => {});
  }, [targetId, sourceId]);

  const confirmar = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API}/cartoes/merge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ source_id: sourceId, target_id: targetId }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Erro');
      onMerged();
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  if (!c1 || !c2) return null;

  const renderCartao = (c, isTarget) => (
    <div style={{
      padding: 12, borderRadius: 8,
      border: `2px solid ${isTarget ? colors.success : colors.border}`,
      background: isTarget ? `${colors.success}10` : colors.bg,
      flex: 1, cursor: 'pointer',
    }} onClick={() => setTargetId(c.id)}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <input type="radio" checked={isTarget} onChange={() => setTargetId(c.id)} />
        <span style={{ fontWeight: 700, fontSize: 13 }}>
          {isTarget ? '✓ MANTER' : 'Excluir após mesclar'}
        </span>
      </div>
      <div style={{ fontSize: 13, fontWeight: 600 }}>{c.nome_cartao}</div>
      <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 4 }}>
        ID: {c.id} · Final: {c.numeracao_final || '—'} · {c.banco_nome || c.banco || '—'}
      </div>
      <div style={{ fontSize: 11, color: colors.textMuted }}>
        Fatura: R$ {(c.fatura_atual || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
      </div>
    </div>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 16,
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: colors.card, borderRadius: 12, padding: 0,
        maxWidth: 640, width: '100%', maxHeight: '90vh',
        display: 'flex', flexDirection: 'column',
        border: `1px solid ${colors.border}`,
      }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${colors.border}` }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: colors.text }}>Mesclar 2 cartões</div>
          <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>
            Escolha qual cartão MANTER. O outro será excluído e todos os dados (transações, faturas, conciliações, etc.) serão movidos para o que ficar.
          </div>
        </div>

        <div style={{ padding: 20, overflow: 'auto', flex: 1 }}>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            {renderCartao(c1, targetId === c1.id)}
            {renderCartao(c2, targetId === c2.id)}
          </div>

          {preview && (
            <div style={{
              background: `${colors.info}12`, border: `1px solid ${colors.info}44`,
              borderRadius: 8, padding: 12, fontSize: 12,
            }}>
              <div style={{ fontWeight: 700, marginBottom: 6, color: colors.info }}>📊 Será movido para o cartão mantido:</div>
              <div>• <strong>{preview.counts?.transacoes || 0}</strong> transações</div>
              <div>• <strong>{preview.counts?.faturas || 0}</strong> faturas (PDFs)</div>
              <div>• <strong>{preview.counts?.contas_pagar || 0}</strong> contas a pagar</div>
              <div>• <strong>{preview.counts?.conciliacoes || 0}</strong> conciliações</div>
              <div>• <strong>{preview.counts?.negociacoes || 0}</strong> negociações</div>
            </div>
          )}

          {error && (
            <div style={{
              marginTop: 12, padding: 10, borderRadius: 8,
              background: `${colors.danger}12`, border: `1px solid ${colors.danger}44`,
              color: colors.danger, fontSize: 12,
            }}>❌ {error}</div>
          )}
        </div>

        <div style={{
          padding: '12px 20px', borderTop: `1px solid ${colors.border}`,
          display: 'flex', gap: 8, justifyContent: 'flex-end',
        }}>
          <button onClick={onClose} disabled={loading} style={{
            padding: '8px 16px', background: 'transparent', border: `1px solid ${colors.border}`,
            borderRadius: 6, color: colors.text, fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}>Cancelar</button>
          <button onClick={confirmar} disabled={loading} style={{
            padding: '8px 16px', background: '#8b5cf6', border: 'none',
            borderRadius: 6, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
          }}>
            {loading ? 'Mesclando...' : '🔗 Confirmar mesclagem'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PagamentoAntecipadoModal({ cartao, onClose, onChanged }) {
  const hoje = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
  const [dados, setDados] = useState(null);
  const [form, setForm] = useState({ valor_pago: '', data_pagamento: hoje, mes_referencia: '', forma_pagamento: 'Boleto', observacoes: '' });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);
  const mobile = useIsMobile();
  const [estornarId, setEstornarId] = useState(null);
  const url = `${API}/cartoes/${cartao.id}/pagamentos-antecipados`;

  const carregar = useCallback(() => {
    fetch(url, { headers: authHeaders() })
      .then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.error || 'Erro'); return d; })
      .then(d => {
        setDados(d);
        setForm(f => ({ ...f, mes_referencia: f.mes_referencia || d.mes_ciclo_aberto }));
      })
      .catch(e => setErro(e.message));
  }, [url]);

  useEffect(() => { carregar(); }, [carregar]);

  const set = (campo) => (e) => setForm(f => ({ ...f, [campo]: e.target.value }));
  const mesBR = (m) => (m ? m.split('-').reverse().join('/') : '-');

  const salvar = async () => {
    setSalvando(true);
    setErro(null);
    try {
      const r = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ ...form, valor_pago: Number(String(form.valor_pago).replace(',', '.')) }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Erro');
      setForm(f => ({ ...f, valor_pago: '', observacoes: '' }));
      carregar();
      onChanged();
    } catch (e) {
      setErro(e.message);
    }
    setSalvando(false);
  };

  const estornar = async () => {
    const id = estornarId;
    setEstornarId(null);
    setErro(null);
    try {
      const r = await fetch(`${url}/${id}`, { method: 'DELETE', headers: authHeaders() });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Erro');
      carregar();
      onChanged();
    } catch (e) {
      setErro(e.message);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={`Pagamento antecipado — ${cartao.nome_cartao}`} width={640}>
      <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 12 }}>
        Boleto ou PIX pago antes de a fatura fechar. Libera o limite na hora. Quando a fatura do ciclo
        chegar, o banco já desconta esse valor do total, por isso ele não entra na quitação da conta a pagar.
        Pagamento de fatura já fechada é lançado em Contas a Pagar.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: mobile ? 'minmax(0, 1fr)' : '1fr 1fr', gap: mobile ? 0 : 12 }}>
        <FormField label="Valor pago" required>
          <Input type="number" step="0.01" min="0" value={form.valor_pago} onChange={set('valor_pago')} placeholder="0,00" />
        </FormField>
        <FormField label="Data do pagamento" required>
          <Input type="date" value={form.data_pagamento} onChange={set('data_pagamento')} />
        </FormField>
        <FormField label={`Ciclo da fatura (aberto: ${mesBR(dados?.mes_ciclo_aberto)})`}>
          <Input type="month" value={form.mes_referencia} onChange={set('mes_referencia')} />
        </FormField>
        <FormField label="Forma">
          <Input type="text" value={form.forma_pagamento} onChange={set('forma_pagamento')} placeholder="Boleto, PIX..." />
        </FormField>
      </div>
      <FormField label="Observação">
        <Input type="text" value={form.observacoes} onChange={set('observacoes')} />
      </FormField>
      {erro && <div style={{ color: colors.danger, fontSize: 12, marginBottom: 8 }}>{erro}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 16 }}>
        <Button variant="outline" color={colors.textMuted} onClick={onClose}>Fechar</Button>
        <Button onClick={salvar} disabled={salvando || !(Number(String(form.valor_pago).replace(',', '.')) > 0)}>
          {salvando ? 'Salvando...' : 'Lançar pagamento'}
        </Button>
      </div>

      <div style={{ fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 6 }}>Antecipados lançados</div>
      {!dados ? (erro ? null : <LoadingSpinner />) : dados.pagamentos.length === 0 ? (
        <div style={{ fontSize: 12, color: colors.textDim }}>Nenhum pagamento antecipado neste cartão.</div>
      ) : (
        <Table>
          <thead>
            <tr><Th>Data</Th><Th>Ciclo</Th><Th>Forma</Th><Th align="right">Valor</Th><Th /></tr>
          </thead>
          <tbody>
            {dados.pagamentos.map(p => (
              <TrHover key={p.id}>
                <Td>{String(p.data_pagamento).slice(0, 10).split('-').reverse().join('/')}</Td>
                <Td>{mesBR(p.mes_referencia)}</Td>
                <Td>{p.forma_pagamento || '-'}</Td>
                <Td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(p.valor_pago)}</Td>
                <Td style={{ textAlign: 'right' }}>
                  <button onClick={() => setEstornarId(p.id)} title="Estornar"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'inline-flex', color: colors.danger }}>
                    <Trash2 size={14} />
                  </button>
                </Td>
              </TrHover>
            ))}
          </tbody>
        </Table>
      )}

      <ConfirmDialog
        isOpen={!!estornarId} onClose={() => setEstornarId(null)} onConfirm={estornar}
        title="Estornar pagamento antecipado" message="O valor volta a consumir o limite do cartão."
      />
    </Modal>
  );
}
